import {
  ECheckType,
  permissionLevel,
  RecommendationProvider,
  workPhases
} from '@publishing-engine/domain/models';
import { z } from 'zod';

const roleConfigValidator = z.object({
  name: z
    .string()
    .regex(
      /^[A-Za-z]+$/,
      'Role name must be a single word containing only letters'
    )
    .trim()
    .min(1)
    .max(50),
  displayName: z.string().trim().min(1).max(50),
  permissions: z.record(z.enum(workPhases), z.enum(permissionLevel)),
  permissionsGroup: z
    .record(z.enum(workPhases), z.enum(permissionLevel))
    .optional()
});

export const getRoleConfigValidator = (
  context: 'frontend' | 'backend' = 'backend'
) =>
  roleConfigValidator.refine(
    data =>
      context === 'backend'
        ? !!Object.keys(data.permissions).length
        : !!Object.keys(data.permissionsGroup || {}).length,
    {
      message: 'At least one phase access level must be selected',
      path: ['permissions']
    }
  );

const updateRoleConfigBackendValidator = z.object({
  displayName: z.string().trim().min(1).max(50),
  permissions: z.record(z.enum(workPhases), z.enum(permissionLevel))
});

const updateRoleConfigFrontendValidator = z.object({
  displayName: z.string().trim().min(1).max(50),
  permissions: z.record(z.enum(workPhases), z.enum(permissionLevel)).optional(),
  permissionsGroup: z
    .record(z.enum(workPhases), z.enum(permissionLevel))
    .optional()
});

export function getUpdateRoleConfigValidator(
  context: 'backend'
): ReturnType<typeof updateRoleConfigBackendValidator.refine>;
export function getUpdateRoleConfigValidator(
  context: 'frontend'
): ReturnType<typeof updateRoleConfigFrontendValidator.refine>;
export function getUpdateRoleConfigValidator(
  context: 'frontend' | 'backend' = 'backend'
) {
  return context === 'backend'
    ? updateRoleConfigBackendValidator.refine(
        data => !!Object.keys(data.permissions).length,
        {
          message: 'At least one phase access level must be selected',
          path: ['permissions']
        }
      )
    : updateRoleConfigFrontendValidator.refine(
        data => !!Object.keys(data.permissionsGroup || {}).length,
        {
          message: 'At least one phase access level must be selected',
          path: ['permissions']
        }
      );
}

const singleRoleAssignmentValidator = z.object({
  workspaceMembershipId: z.string(),
  submissionContextId: z.string(),
  roleName: z
    .string()
    .min(1)
    .regex(/^((?!((^|, )(Select))+$).)*$/)
});

const issnPattern = /^\d{4}[\u002D\u2013\u2012\u2011]?\d{3}[\dXx]$/;

const journalEmailAddressSchema = z.object({
  email: z
    .string()
    .min(1, { message: 'Email is required.' })
    .email({ message: 'Invalid email address.' }),
  name: z.string().optional()
});

/**
 * A list of journal addresses that rejects duplicate entries. Pass
 * `requiredMessage` for a list the journal cannot go without, which also makes
 * the list itself mandatory rather than merely optional.
 */
const journalEmailAddressList = <T extends { email: string }>(
  schema: z.ZodType<T>,
  message: string,
  requiredMessage?: string
) =>
  z.array(schema).superRefine((items, ctx) => {
    if (requiredMessage && !items.length) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: requiredMessage
      });
    }

    const seen = new Set<string>();
    items.forEach((item, index) => {
      const key = item.email.trim().toLowerCase();
      if (seen.has(key)) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: [index, 'email'],
          message
        });
      } else {
        seen.add(key);
      }
    });
  });

const journalEmailsConfigSchema = z
  .object({
    additionalEmails: journalEmailAddressList(
      journalEmailAddressSchema,
      'Duplicate recipient email.'
    ).optional(),
    fromEmail: journalEmailAddressSchema.optional(),
    replyTo: journalEmailAddressList(
      journalEmailAddressSchema,
      'Duplicate reply-to email.',
      'At least one reply-to email is required.'
    )
  })
  .optional();

// Option strings double as schema enum values and decision-template keys, so
// duplicates would leave entries with no matching template.
const optionList = (requiredMessage?: string) =>
  z.array(z.string()).superRefine((options, ctx) => {
    if (requiredMessage && !options.length) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: requiredMessage
      });
    }

    const seen = new Set<string>();
    options.forEach((option, index) => {
      if (seen.has(option)) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: [index],
          message: 'Duplicate option.'
        });
      } else {
        seen.add(option);
      }
    });
  });

export const journalSettingValidator = z.object({
  data: z
    .object({
      name: z
        .string()
        .min(1, { message: 'Journal name cannot be empty.' })
        .optional(),
      description: z
        .string()
        .min(1, { message: 'Description cannot be empty.' })
        .optional(),
      introduction: z
        .string()
        .min(1, { message: 'Introduction cannot be empty.' })
        .optional(),
      shortname: z
        .string()
        .min(1, { message: 'Journal code cannot be empty.' })
        .optional()
    })
    .optional(),

  config: z
    .object({
      email: z
        .string()
        .min(1, { message: 'Please enter a journal email.' })
        .email()
        .optional(),

      url: z
        .string()
        .min(1, { message: 'Please enter a journal URL.' })
        .url()
        .optional(),

      issn: z
        .object({
          print: z
            .string()
            .optional()
            .refine(val => !val || issnPattern.test(val), {
              message: 'Invalid ISSN format. Expected format: XXXX-XXXX'
            }),

          online: z
            .string()
            .optional()
            .refine(val => !val || issnPattern.test(val), {
              message: 'Invalid ISSN format. Expected format: XXXX-XXXX'
            })
        })
        .optional(),

      publishingModel: z
        .enum(['subscription', 'openAccess', 'hybrid'])
        .optional(),

      // One entry per article workflow — each article type can use a different model.
      peerReviewModel: z
        .array(
          z.object({
            articleWorkflowId: z.string(),
            model: z.enum([
              'doubleAnonymous',
              'singleAnonymous',
              'openPeerReview'
            ])
          })
        )
        .optional(),

      editorialWithdrawalOptions: optionList(
        'Select at least one withdrawal option.'
      ).optional(),

      authorWithdrawalOptions: optionList(
        'Select at least one withdrawal option.'
      ).optional(),

      transferOptions: optionList(
        'Select at least one transfer option.'
      ).optional(),

      preRejectionOptions: optionList(
        'Select at least one rejection option.'
      ).optional(),

      postRejectionOptions: optionList(
        'Select at least one rejection option.'
      ).optional(),

      revisionSeverityOptions: z.array(z.string()).optional(),
      reviewerSearch: z.boolean().optional(),
      assignReviewers: z.boolean().optional(),
      reviewerRating: z.boolean().optional(),
      isCoReviewEnabled: z.boolean().optional(),
      isCOCRecommendationRequired: z.boolean().optional(),
      logoUrl: z.string().url('Must be a valid URL').optional(),
      cocConfig: z
        .array(
          z.object({
            role: z.string().min(1),
            roleAbbreviation: z.string().optional(),
            roleLevel: z.number().optional(),
            autoAssign: z.boolean(),
            onlyAssignedSubmissionVisibility: z.boolean().optional(),
            isOwner: z.boolean(),
            isDecisionMaker: z.boolean(),
            roleMaxLimit: z.number().optional(),
            maxRecommendationRequiredCount: z.number().optional(),
            decision: z
              .array(
                z.object({
                  value: z.string(),
                  label: z.string()
                })
              )
              .optional()
          })
        )
        .optional(),
      integrityChecks: z
        .array(
          z.object({
            event: z.string(),
            articleChecks: z
              .array(
                z.object({
                  articleWorkflowId: z.string(),
                  checks: z.array(
                    z.object({
                      checkType: z.nativeEnum(ECheckType),
                      runType: z.string()
                    })
                  )
                })
              )
              .optional(),
            allowedChecks: z
              .array(
                z.object({
                  role: z.string(),
                  checks: z.array(z.nativeEnum(ECheckType))
                })
              )
              .optional()
          })
        )
        .optional(),
      lexiconProjectId: z.string().optional(),
      lexiconClassId: z.string().optional(),
      reviewerRecommendationProviders: z
        .array(
          z.object({
            provider: z.nativeEnum(RecommendationProvider),
            enable: z.boolean()
          })
        )
        .optional(),
      cms: z
        .object({
          authorSubmissionForm: z
            .object({
              startSubmissionForm: z
                .object({
                  header: z
                    .string()
                    .trim()
                    .min(1, { message: 'Title cannot be empty.' }),
                  subHeader: z
                    .string()
                    .trim()
                    .min(1, { message: 'Description cannot be empty.' })
                })
                .optional(),
              reviewSubmissionInformation: z
                .object({
                  header: z
                    .string()
                    .trim()
                    .min(1, { message: 'Title cannot be empty.' }),
                  subHeader: z
                    .string()
                    .trim()
                    .min(1, { message: 'Description cannot be empty.' })
                })
                .optional(),
              reviewAuthorInformation: z
                .object({
                  header: z
                    .string()
                    .trim()
                    .min(1, { message: 'Title cannot be empty.' }),
                  subHeader: z
                    .string()
                    .trim()
                    .min(1, { message: 'Description cannot be empty.' })
                })
                .optional(),
              submissionPreferences: z
                .object({
                  header: z
                    .string()
                    .trim()
                    .min(1, { message: 'Header cannot be empty.' }),
                  subHeader: z
                    .string()
                    .trim()
                    .min(1, { message: 'SubHeader cannot be empty.' })
                })
                .optional()
            })
            .optional(),
          lexicon: z
            .object({
              reviewerActionCard: z
                .object({
                  createAccount: z.boolean().optional()
                })
                .optional()
            })
            .optional()
        })
        .optional(),
      isUADEnable: z.boolean().optional(),
      boardMemberReviewerAssigment: z.boolean().optional(),
      enableConsolidatedPdf: z.boolean().optional(),
      notifyInvitingEditorsOnReviewTargetMet: z.boolean().optional(),
      journalEmails: journalEmailsConfigSchema
    })
    .optional(),

  isActive: z.boolean().optional()
});

export const newJournalMembershipValidator = z.array(
  singleRoleAssignmentValidator
    .extend({
      selected: z.boolean().default(false),
      roleName: z.string().min(1).default('Select')
    })
    .refine(
      data =>
        data.selected
          ? /^((?!((^|, )(Select))+$).)*$/.test(data.roleName ?? '')
          : true,
      {
        message: 'Role name is required',
        path: ['roleName']
      }
    )
);

export const updateJournalMembershipValidator =
  singleRoleAssignmentValidator.extend({ assignmentId: z.string() });

export const focusIssueGuidelineValidator = z.object({
  guidelineLink: z.string().url({ message: 'Issue link should be valid' })
});
